import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Fab,
  Tooltip,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
  Slide,
} from "@mui/material";
import {
  Remove as MinimizeIcon,
  Menu as MenuIcon,
  AutoAwesomeOutlined as AutoAwesomeOutlinedIcon,
  ChevronLeft as ChevronLeftIcon,
} from "@mui/icons-material";
import { formatTheme } from "@/theme/formatTheme";
import {
  AIChatInterface,
  CustomCircleIconButton,
} from "@ippon/shared-components";
import { propertyDocumentGroups } from "@/constants/documentGroups";
import { ShrinkIcon, ExpandIcon } from "@ippon/shared-assets";
import { useSidebarWidth } from "@ippon/shared-layouts";
import { getPageContext, refetchBus, buildRefetchKey } from "@ippon/shared-utils";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  sendChatMessage,
  confirmChatAction,
  rejectChatAction,
  addUserMessage,
  getChatMessages,
  deleteChatMessages,
  getChatThreads,
  removeAssistantMessageById,
  clearMessages,
  warmupAgent,
  consumeChatOpen,
  type GetThreadsResponse,
  type ReferencedDocument,
} from "@/store/chatSlice";
import CustomThreadSidebar from "./CustomThreadSidebar";

// 物件書類の document_type(コード) → 日本語ラベル の対応表（カードのラベル表示用）
const PROPERTY_DOC_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  propertyDocumentGroups.flatMap((g) =>
    g.categories.map((c) => [c.key, c.label] as const)
  )
);

// 参照書類カードのサブ行に出す「書類ラベル名」を entity_label に載せる。
// 物件書類: document_type(コード)→日本語ラベル(例: 建築確認通知書)。
// 契約/修繕/支出: 上段(document_name)が既に書類名(例: 身分証明書)なのでラベルは付けない
// （サブ行は物件名のみ）。
const withReferencedDocLabels = (
  docs: ReferencedDocument[] | undefined
): ReferencedDocument[] => {
  if (!docs) return [];
  return docs.map((doc) => {
    const isProperty = !doc.source_type || doc.source_type === "property_doc";
    const entity_label = isProperty
      ? PROPERTY_DOC_TYPE_LABELS[doc.document_type]
      : undefined;
    return { ...doc, entity_label };
  });
};
import { setSidebarOpen } from "@/store/sidebarSlice";
import { RootState } from "@/store";
import type { MessageType, FileAttachment } from "@ippon/shared-components";

type AbortablePromise<T> = Promise<T> & { abort: (reason?: string) => void };

const AIChatButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isThreadSidebarOpen, setIsThreadSidebarOpen] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<
    string | undefined
  >();
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  // 開く/閉じるアニメーション用。origin は transform-origin(起点)。
  const [chatOrigin, setChatOrigin] = useState("bottom right");
  const [isClosing, setIsClosing] = useState(false);
  const location = useLocation();
  const dispatch = useAppDispatch();
  const sendPromiseRef = React.useRef<AbortablePromise<any> | null>(null);
  // pendingChatOpen effect から「開いた直後の送信」を呼ぶための最新 handleSendMessage 参照。
  const handleSendMessageRef = React.useRef<
    ((content: string, files: FileAttachment[]) => void) | null
  >(null);
  const mobileThreadListRef = React.useRef<HTMLDivElement>(null);
  const mobileThreadBottomObserverRef = React.useRef<HTMLDivElement>(null);
  const latestAssistantMessageIdRef = React.useRef<string | null>(null);
  const latestUserMessageContentRef = React.useRef<string | null>(null);
  const prevIsExpandedRef = React.useRef<boolean>(false);
  const prevPathnameRef = React.useRef<string>(location.pathname);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"), {
    noSsr: true,
  });
  const isMobile = useMediaQuery(theme.breakpoints.down("md"), { noSsr: true });

  const sidebarWidth = useSidebarWidth();

  // Redux state
  const {
    messages: chatMessages,
    isSending,
    hasMore,
    isLoading,
    nextToken,
    threads,
    isLoadingThreads,
    threadsNextToken,
    threadsHasMore,
    pendingChatOpen,
    messagesError,
  } = useAppSelector((state: RootState) => state.chat);

  const { isOpen: isSidebarOpen } = useAppSelector(
    (state: RootState) => state.sidebar,
  );

  // 必要に応じて物件IDを設定（例：現在表示中の物件ページから取得）
  const propertyId = undefined; // TODO: 物件ページから取得する場合は設定

  // ログイン画面や認証関連ページでは表示しない。
  const hideOnPaths = [
    "/login",
    "/checkmail",
    "/otp",
    "/reset_password",
  ];

  const shouldHide = hideOnPaths.includes(location.pathname);

  // ホームは画面自体がチャット起点なので、右下の Fab（閉じている時のボタン）は出さない。
  // ただしコンポーネントは生かし、Home の入力から送信された時は開けるようにする。
  const hideFab = location.pathname === "/home";

  // 外部（ホームのランチャー等）からチャットを開く連携。
  // pendingChatOpen が立ったら、チャットを開き、必要なら全画面化・スレッド読込・即時送信して消費する。
  useEffect(() => {
    if (!pendingChatOpen || shouldHide) return;
    const { threadId, sendQuery, expand, originX, originY } = pendingChatOpen;
    // 起点座標（Home の入力位置など）があれば、そこから広がるように開く。
    // 全画面パネル≒ビューポートなので、ビューポート内の割合を transform-origin にする
    // （transform-origin は要素基準なので px 直指定はズレる）。
    if (typeof originX === "number" && typeof originY === "number") {
      const xPct = Math.max(0, Math.min(100, (originX / window.innerWidth) * 100));
      const yPct = Math.max(0, Math.min(100, (originY / window.innerHeight) * 100));
      setChatOrigin(`${xPct}% ${yPct}%`);
    } else {
      setChatOrigin("bottom right");
    }
    setIsClosing(false);
    void dispatch(warmupAgent());
    setIsOpen(true);
    if (expand) setIsExpanded(true);
    if (threadId) {
      setSelectedThreadId(threadId);
      dispatch(getChatMessages({ thread_id: threadId, limit: 20 }));
    } else {
      dispatch(clearMessages());
      setSelectedThreadId(undefined);
    }
    dispatch(consumeChatOpen());
    // 開いた直後に送信（ホームのランチャー）。state 反映後に送るため次tickで実行。
    if (sendQuery && sendQuery.trim()) {
      const q = sendQuery;
      setTimeout(() => handleSendMessageRef.current?.(q, []), 0);
    }
  }, [pendingChatOpen, shouldHide, dispatch]);

  // チャットが開かれたときの自動 fetch は廃止。
  // 旧実装は「初回開時 / 新規チャット押下後」に getChatMessages を自動 dispatch していたが、
  // - 「新規チャット = 完全に空のスレッドから始める」なので fetch 不要
  // - 既存スレッドを見たい時は CustomThreadSidebar 上でのクリックで明示的に dispatch される
  // 必要があれば過去履歴は thread 単位で個別ロードする。

  // 読み込み完了時にフラグをリセット
  useEffect(() => {
    if (!isLoading && isLoadingMoreMessages) {
      setIsLoadingMoreMessages(false);
    }
  }, [isLoading, isLoadingMoreMessages]);

  // 大画面の時はデフォルトでスレッドサイドバーを表示
  useEffect(() => {
    if (isExpanded && isLargeScreen) {
      setIsThreadSidebarOpen(true);
    } else if (!isExpanded) {
      setIsThreadSidebarOpen(false);
    }
  }, [isExpanded, isLargeScreen]);

  // 全画面モードでスレッドサイドバーが開いているときにスレッド一覧を取得
  useEffect(() => {
    if (shouldHide) return; // ログイン画面などでは実行しない
    if (isExpanded && isThreadSidebarOpen) {
      dispatch(
        getChatThreads({
          limit: 15,
        }),
      );
    }
  }, [isExpanded, isThreadSidebarOpen, dispatch, shouldHide]);

  // 縮小画面でスレッド一覧が開いているときにスレッド一覧を取得
  useEffect(() => {
    if (shouldHide) return; // ログイン画面などでは実行しない
    if (!isExpanded && isThreadSidebarOpen) {
      dispatch(
        getChatThreads({
          limit: 15,
        }),
      );
    }
  }, [isExpanded, isThreadSidebarOpen, dispatch, shouldHide]);

  const handleLoadMoreThreads = useCallback(() => {
    if (threadsHasMore && !isLoadingThreads && threadsNextToken) {
      dispatch(
        getChatThreads({
          limit: 15,
          next_token: threadsNextToken,
        }),
      );
    }
  }, [threadsHasMore, isLoadingThreads, threadsNextToken, dispatch]);

  // 縮小画面のスレッド一覧のページネーション
  useEffect(() => {
    if (!mobileThreadListRef.current || !threadsHasMore || isLoadingThreads)
      return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          handleLoadMoreThreads();
        }
      },
      {
        root: mobileThreadListRef.current,
        threshold: 0.1,
      },
    );

    if (mobileThreadBottomObserverRef.current) {
      observer.observe(mobileThreadBottomObserverRef.current);
    }

    return () => {
      if (mobileThreadBottomObserverRef.current) {
        observer.unobserve(mobileThreadBottomObserverRef.current);
      }
    };
  }, [threadsHasMore, isLoadingThreads, handleLoadMoreThreads]);

  // 全画面モードに切り替えた瞬間だけサイドバーを閉じる
  useEffect(() => {
    const wasExpanded = prevIsExpandedRef.current;

    if (!wasExpanded && isExpanded) {
      // 通常モード → 全画面モードに切り替わった時だけサイドバーを閉じる
      // forceCollapsedは使わず、直接setSidebarOpenを使う（ユーザーが手動で開けるように）
      dispatch(setSidebarOpen(false));
    } else if (wasExpanded && !isExpanded) {
      // 全画面モード → 通常モードに戻った時はサイドバーを開く
      dispatch(setSidebarOpen(true));
    }

    prevIsExpandedRef.current = isExpanded;
  }, [isExpanded, dispatch]);

  // サイドバーのメニューアイテムがクリックされたとき、大画面だったら小画面に戻す
  useEffect(() => {
    const currentPathname = location.pathname;
    const prevPathname = prevPathnameRef.current;

    // パスが実際に変更された場合のみ、大画面だったら小画面に戻す
    if (currentPathname !== prevPathname && isExpanded && isOpen) {
      setIsExpanded(false);
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname, isExpanded, isOpen]);

  // チャットが最小化された (= 閉じた) ときの後処理。
  // 旧実装は deleteChatMessages + state リセットで「次に開いたら新規」だったが、
  // 「最後に見ていた画面を復元」する方針に変更。スレッド / メッセージ / 展開状態
  // を破棄せず保持する。ページリロード時は Redux 初期化で結果的にリフレッシュされる。
  useEffect(() => {
    if (!isOpen) {
      // 全画面モードだった場合のみサイドバーを開く (チャット最大化中はサイドバー
      // を畳んでいたため、最小化時に戻す)
      if (prevIsExpandedRef.current) {
        dispatch(setSidebarOpen(true));
      }
    }
  }, [isOpen, dispatch]);

  // 閉じるアニメーション: 縮小してから unmount する。
  const CLOSE_ANIM_MS = 200;
  const closeChat = (after?: () => void) => {
    setIsClosing(true);
    window.setTimeout(() => {
      setIsClosing(false);
      setIsOpen(false);
      after?.();
    }, CLOSE_ANIM_MS);
  };

  const handleClick = () => {
    if (!isOpen) {
      // 開く時: Fab は右下にあるので右下を起点にして広がるように出す。
      setChatOrigin("bottom right");
      setIsClosing(false);
      void dispatch(warmupAgent());
      setIsOpen(true);
    } else {
      // 閉じる時（クリーンアップはuseEffectで行う）
      closeChat();
    }
  };

  const handleNewChat = () => {
    // 新規チャット = 「新しい thread を開始する UI 状態にする」だけで十分。
    // 過去 thread の messages を BE から消す必要は無いので、API は呼ばずに
    // ローカル state (messages / selectedThreadId) のみリセットする。
    dispatch(clearMessages());
    setSelectedThreadId(undefined);
    setIsOpen(true);
  };

  const handleSendMessage = async (
    content: string,
    files: FileAttachment[],
  ) => {
    if (!content.trim() && files.length === 0) return;

    dispatch(
      addUserMessage({
        content,
        files: files.map((f) => ({
          id: f.id,
          name: f.name,
          type: f.type,
          url: f.url,
        })),
      }),
    );

    latestUserMessageContentRef.current = content;

    const fileObjects = files
      .map((f) => f.file)
      .filter((f): f is File => f !== undefined);

    const wasNewThread = !selectedThreadId;

    // 送信時点での画面コンテキストを抽出 (data-entity/data-id/data-field)。
    // entity 名をハードコードせず、画面側が data 属性で宣言したものをそのまま渡す。
    const pageContext = getPageContext();

    const promise = dispatch(
      sendChatMessage({
        query: content,
        thread_id: selectedThreadId,
        property_id: propertyId,
        files: fileObjects.length > 0 ? fileObjects : undefined,
        pageContext,
      }),
    );
    sendPromiseRef.current = promise;

    try {
      const result = await promise;

      // 成功時の処理
      if (result.type === "chat/sendMessage/fulfilled") {
        // 画面コンテキストの entity/id を refetchBus に通知 → 該当画面が自動再フェッチ。
        // reducer 内ではなくここで emit する (reducer 内同期 emit + dispatch のレース回避)。
        if (pageContext && pageContext.entities.length > 0) {
          for (const e of pageContext.entities) {
            if (e.entity && e.id) {
              refetchBus.emit(buildRefetchKey(e.entity, e.id));
            }
          }
        }

        const payload = result.payload as {
          message: any;
          response: { thread_id?: string; pending_action?: { action_id: string } };
        };
        if (payload.response.thread_id) {
          const newThreadId = payload.response.thread_id;
          setSelectedThreadId(newThreadId);

          if (wasNewThread) {
            // 新規スレッド作成時はスレッドリストを更新
            await new Promise((resolve) => setTimeout(resolve, 800));
            dispatch(
              getChatThreads({
                limit: 15,
              }),
            );
            // 承認待ちのときは getChatMessages で pending_action が消えるため再取得しない
            if (!payload.response.pending_action) {
              dispatch(
                getChatMessages({
                  thread_id: newThreadId,
                  limit: 20,
                }),
              );
            }
          }
        }
      }
    } catch {
      // キャンセルやエラー時は何もしない（isSending は chatSlice で false になる）
    }
  };

  // pendingChatOpen effect から最新の送信関数を呼べるように、毎レンダーで参照を更新。
  handleSendMessageRef.current = handleSendMessage;

  const handleCancelSend = async () => {
    const wasNewThread = !selectedThreadId;
    const initialTopThreadId = threads.length > 0 ? threads[0].thread_id : null;
    const p = sendPromiseRef.current;

    if (p && typeof p.abort === "function") {
      p.abort();
    }

    // 記録された最新のアシスタントメッセージIDがあれば、そのメッセージを削除
    if (latestAssistantMessageIdRef.current) {
      dispatch(
        removeAssistantMessageById({
          messageId: latestAssistantMessageIdRef.current,
        }),
      );
      latestAssistantMessageIdRef.current = null;
    }

    // 既存スレッドの場合は、バックエンド側でもアシスタントメッセージを削除
    if (!wasNewThread && selectedThreadId) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      await dispatch(
        deleteChatMessages({
          thread_id: selectedThreadId,
          role: "assistant",
          latest_user_message_content:
            latestUserMessageContentRef.current || undefined,
        }),
      );
      latestUserMessageContentRef.current = null;
      dispatch(getChatThreads({ limit: 15 }));
      dispatch(
        getChatMessages({
          thread_id: selectedThreadId,
          limit: 20,
        }),
      );
      return;
    }

    // 新規スレッドの場合は、作成されてしまったらアシスタントメッセージのみを削除
    if (wasNewThread) {
      for (let i = 0; i < 3; i++) {
        await new Promise((resolve) => setTimeout(resolve, 1500));

        const result = await dispatch(getChatThreads({ limit: 15 }));

        if (result.type === "chat/getThreads/fulfilled") {
          const payload = result.payload as GetThreadsResponse;
          if (payload.threads && payload.threads.length > 0) {
            const currentTopThreadId = payload.threads[0].thread_id;

            if (currentTopThreadId !== initialTopThreadId) {
              // 新スレッドが作成されていたら、そのスレッドを選択
              setSelectedThreadId(currentTopThreadId);

              // バックエンド側でアシスタントメッセージを削除
              await dispatch(
                deleteChatMessages({
                  thread_id: currentTopThreadId,
                  role: "assistant",
                  latest_user_message_content:
                    latestUserMessageContentRef.current || undefined,
                }),
              );
              latestUserMessageContentRef.current = null;
              dispatch(
                getChatMessages({
                  thread_id: currentTopThreadId,
                  limit: 20,
                }),
              );
              return;
            }
          }
        }
      }
    } else {
      dispatch(getChatThreads({ limit: 15 }));
    }
  };

  const handleConfirmAction = useCallback(
    (messageId: string, actionId: string) => {
      dispatch(
        confirmChatAction({
          action_id: actionId,
          source_message_id: messageId,
        }),
      );
    },
    [dispatch],
  );

  const handleRejectAction = useCallback(
    (messageId: string, actionId: string) => {
      dispatch(
        rejectChatAction({
          action_id: actionId,
          source_message_id: messageId,
        }),
      );
    },
    [dispatch],
  );

  const handleLoadMore = () => {
    if (hasMore && !isLoading && nextToken) {
      setIsLoadingMoreMessages(true);
      dispatch(
        getChatMessages({
          limit: 15,
          next_token: nextToken,
          thread_id: selectedThreadId,
        }),
      );
    }
  };

  const convertedMessages: MessageType[] = chatMessages.map((msg) => {
    if (
      msg.role === "user" &&
      msg.referenced_documents &&
      msg.referenced_documents.length > 0 &&
      (!msg.files || msg.files.length === 0)
    ) {
      return {
        id: msg.message_id,
        content: msg.content,
        sender: "user" as const,
        timestamp: new Date(msg.timestamp),
        files: msg.referenced_documents.map((doc) => ({
          id: doc.document_id,
          name: doc.document_name,
          type: doc.document_type,
          url: doc.file_url,
        })),
        referencedDocuments: undefined,
        confidence: msg.confidence,
      };
    }

    return {
      id: msg.message_id,
      content: msg.content,
      sender: (msg.role === "user" ? "user" : "other") as "user" | "other",
      timestamp: new Date(msg.timestamp),
      files: msg.files,
      referencedDocuments: withReferencedDocLabels(msg.referenced_documents),
      confidence: msg.confidence,
      pendingAction: msg.pending_action
        ? {
            actionId: msg.pending_action.action_id,
            toolName: msg.pending_action.tool_name,
            status: msg.pending_action.status,
          }
        : undefined,
    };
  });

  if (shouldHide) {
    return null;
  }

  return (
    <>
      {/* チャットボタン（ホームでは非表示） */}
      {!isOpen && !hideFab && (
        <Box
          sx={{
            position: "fixed",
            bottom: 35,
            right: 24,
            zIndex: 1300,
          }}
        >
          <Tooltip title="IPPON AIエージェント" placement="left">
            <Fab
              color="primary"
              aria-label="AI chatbot"
              onClick={handleClick}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: formatTheme.colors.primary.main,
                color: "#FFFFFF",
                boxShadow: "0px 4px 12px rgba(14, 157, 188, 0.3)",
                transition: "all 0.3s ease",
                "&:hover": {
                  backgroundColor: formatTheme.colors.primary.hover,
                  boxShadow: "0px 6px 16px rgba(14, 157, 188, 0.4)",
                  transform: "scale(1.05)",
                },
                "&:active": {
                  backgroundColor: formatTheme.colors.primary.active,
                  transform: "scale(0.98)",
                },
              }}
            >
              <AutoAwesomeOutlinedIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}

      {/* AI Chat Interface */}
      {isOpen && (
        <>
          {/* 全画面モード時のオーバーレイ */}
          {isExpanded && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                right: 0,
                bottom: 0,
                // サイドバーは常に存在するので、常にsidebarWidth分を避ける
                left: {
                  xs: 0,
                  sm: isSidebarOpen ? `${sidebarWidth}px` : "60px",
                },
                backgroundColor:
                  formatTheme.components?.modal?.overlay ||
                  "rgba(0, 0, 0, 0.5)",
                // PreviewDrawer (1200) より上に出すためボタンと同階層 (1300) に揃える
                zIndex: 1300,
                display: { xs: "none", sm: "block" },
                // パネルと同調してフェードイン/アウト。
                animation: isClosing
                  ? "aichatOverlayOut 0.2s ease forwards"
                  : "aichatOverlayIn 0.26s ease",
                "@keyframes aichatOverlayIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
                "@keyframes aichatOverlayOut": {
                  from: { opacity: 1 },
                  to: { opacity: 0 },
                },
              }}
            />
          )}
          <Box
            sx={{
              position: "fixed",
              // PreviewDrawer (1200) より上に出すためボタンと同階層 (1300) に揃える
              zIndex: 1300,
              display: "flex",
              flexDirection: "column",
              // 拡大/縮小どちらも top/left/right/bottom の4辺で位置指定し、
              // width/height は導出させる。これで全辺がトランジションで補間され、
              // 縦横ともに滑らかに拡大・縮小する（auto を挟まないのがポイント）。
              ...(isExpanded
                ? {
                    top: { xs: 0, sm: "24px" },
                    right: { xs: 0, sm: "24px" },
                    bottom: { xs: 0, sm: "24px" },
                    left: {
                      xs: 0,
                      sm: isMobile
                        ? "24px"
                        : isSidebarOpen
                          ? `calc(${sidebarWidth}px + 24px)`
                          : "84px", // 閉じている時は60px + 24px
                    },
                    borderRadius: { xs: 0, sm: "12px" },
                    boxShadow: {
                      xs: "none",
                      sm: "0px 8px 32px rgba(0, 0, 0, 0.3)",
                    },
                  }
                : {
                    // 縮小時: 450x600 を右下 24px マージンで。4辺を具体値で表す。
                    top: { xs: 0, sm: "calc(100vh - 600px - 24px)" },
                    left: { xs: 0, sm: "calc(100vw - 450px - 24px)" },
                    right: { xs: 0, sm: "24px" },
                    bottom: { xs: 0, sm: "24px" },
                    borderRadius: { xs: 0, sm: "12px" },
                    boxShadow: {
                      xs: "none",
                      sm: "0px 8px 32px rgba(0, 0, 0, 0.12)",
                    },
                  }),
              backgroundColor: "#FFFFFF",
              overflow: "hidden",
              // 開く/閉じる: 起点(Fab/入力位置)から拡大・縮小して出入りする。
              transformOrigin: chatOrigin,
              animation: isClosing
                ? "aichatClose 0.2s ease forwards"
                : "aichatOpen 0.26s cubic-bezier(0.16, 1, 0.3, 1)",
              // 拡大/縮小(サイズ変化)は4辺をトランジションで滑らかに補間する。
              transition:
                "top 0.26s ease, left 0.26s ease, right 0.26s ease, bottom 0.26s ease, border-radius 0.26s ease",
              "@keyframes aichatOpen": {
                from: { transform: "scale(0.5)", opacity: 0 },
                to: { transform: "scale(1)", opacity: 1 },
              },
              "@keyframes aichatClose": {
                from: { transform: "scale(1)", opacity: 1 },
                to: { transform: "scale(0.5)", opacity: 0 },
              },
            }}
          >
            {/* ヘッダー */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 0.5,
                borderBottom: `1px solid ${theme.palette.divider}`,
                backgroundColor: "#FAFAFA",
              }}
            >
              {/* ハンバーガーメニュー/戻るボタンとタイトル */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {(!isExpanded || (isExpanded && isMobile)) &&
                  (isThreadSidebarOpen ? (
                    <CustomCircleIconButton
                      ariaLabel="戻る"
                      onClick={() => setIsThreadSidebarOpen(false)}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 24 }} />
                    </CustomCircleIconButton>
                  ) : (
                    <Tooltip title="履歴" placement="top">
                      <IconButton
                        size="small"
                        onClick={() => setIsThreadSidebarOpen(true)}
                        sx={{
                          color: "#3E3E3E",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        <MenuIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  ))}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 20,
                    height: 20,
                    borderRadius: "6px",
                    background:
                      "linear-gradient(135deg, #0B9DBD 0%, #5CC8E0 100%)",
                    color: "#FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 1px 4px rgba(11, 157, 189, 0.25)",
                  }}
                >
                  <AutoAwesomeOutlinedIcon sx={{ fontSize: 13 }} />
                </Box>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#3E3E3E",
                    fontWeight: 700,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  IPPON AI エージェント
                </Typography>
              </Box>
              {/* 右側のボタン群 */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                {/* 新規チャットはヘッダーから廃止し、履歴（スレッド一覧）の上に配置した。 */}
                {/* 展開/縮小ボタン */}
                <Tooltip
                  title={isExpanded ? "縮小画面" : "全画面"}
                  placement="top"
                >
                  <IconButton
                    onClick={() => setIsExpanded(!isExpanded)}
                    size="small"
                    sx={{
                      color: "#3E3E3E",
                      display: { xs: "none", sm: "inline-flex" },
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {isExpanded ? <ShrinkIcon /> : <ExpandIcon />}
                  </IconButton>
                </Tooltip>
                {/* 最小化ボタン: スレッド・メッセージ状態は破棄せず、次に開いた時に
                    最後に見ていた画面 (selectedThreadId / expanded / pending action 等)
                    を復元する。ページリロード時は Redux が初期化されるので結果的に
                    リフレッシュされる挙動。 */}
                <Tooltip title="最小化" placement="top">
                  <IconButton
                    onClick={() => {
                      closeChat();
                    }}
                    size="small"
                    sx={{
                      color: "#3E3E3E",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    <MinimizeIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* チャットコンテンツ */}
            <Box
              sx={{
                flex: 1,
                position: "relative",
                overflow: "hidden",
                display: "flex",
              }}
            >
              {/* スレッド一覧が開いている時の背景オーバーレイ（クリックでスレッド閉じる） */}
              {((!isExpanded && isThreadSidebarOpen) ||
                (isExpanded && isMobile && isThreadSidebarOpen)) && (
                <Box
                  onClick={() => setIsThreadSidebarOpen(false)}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9,
                    backgroundColor: "transparent",
                  }}
                />
              )}
              {/* 縮小画面または全画面モードでmd以下の時のスレッド一覧 */}
              <Slide
                direction="right"
                in={
                  (!isExpanded && isThreadSidebarOpen) ||
                  (isExpanded && isMobile && isThreadSidebarOpen)
                }
                mountOnEnter
                unmountOnExit
                timeout={250}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "280px",
                    bottom: 0,
                    zIndex: 10,
                  }}
                >
                  <CustomThreadSidebar
                    threads={threads}
                    selectedThreadId={selectedThreadId}
                    onThreadSelect={(threadId: string) => {
                      setSelectedThreadId(threadId);
                      dispatch(
                        getChatMessages({
                          thread_id: threadId,
                          limit: 20,
                        }),
                      );
                    }}
                    onThreadSelectCallback={() => {
                      setIsThreadSidebarOpen(false);
                    }}
                    onNewChat={() => {
                      // 新規チャットでもスレッド一覧(オーバーレイ)を閉じる。
                      handleNewChat();
                      setIsThreadSidebarOpen(false);
                    }}
                    isLoading={isLoadingThreads}
                    hasMore={threadsHasMore}
                    onLoadMore={handleLoadMoreThreads}
                    width={280}
                    padding="6px 16px"
                    useTruncateText={false}
                    scrollContainerRef={mobileThreadListRef}
                    bottomObserverRef={mobileThreadBottomObserverRef}
                    bottomObserverDataAttr="data-thread-bottom-observer"
                  />
                </Box>
              </Slide>
              {/* スレッドサイドバー（全画面モードでmd以上の時のみ常時表示） */}
              {isExpanded && isThreadSidebarOpen && !isMobile && (
                <CustomThreadSidebar
                  threads={threads}
                  selectedThreadId={selectedThreadId}
                  onThreadSelect={(threadId: string) => {
                    setSelectedThreadId(threadId);
                    dispatch(
                      getChatMessages({
                        thread_id: threadId,
                        limit: 20,
                      }),
                    );
                  }}
                  onNewChat={handleNewChat}
                  isLoading={isLoadingThreads}
                  hasMore={threadsHasMore}
                  onLoadMore={handleLoadMoreThreads}
                  width={undefined}
                />
              )}
              <Box sx={{ flex: 1, position: "relative", overflow: "hidden" }}>
                {/* 新規チャット時のガイドバナー（読み込み中/失敗中は出さない） */}
                {convertedMessages.length === 0 &&
                  !isLoading &&
                  !messagesError && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 12,
                      left: 12,
                      right: 12,
                      zIndex: 5,
                      px: 2,
                      py: 1.75,
                      borderRadius: "12px",
                      background:
                        "linear-gradient(135deg, #F4FCFF 0%, #DBF8FF 100%)",
                      border: "1px solid rgba(11, 157, 189, 0.18)",
                      boxShadow: "0 2px 10px rgba(11, 157, 189, 0.08)",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#4B5563",
                          lineHeight: 1.7,
                          letterSpacing: "0.01em",
                        }}
                      >
                        物件の登録・更新など、これまで画面で行っていた操作を、チャットから指示するだけで実行できるAIアシスタントです。日常業務をそのまま会話で進められます。
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: "1px dashed rgba(11, 157, 189, 0.25)",
                          fontSize: "12px",
                          color: "#9CA3AF",
                          lineHeight: 1.6,
                          letterSpacing: "0.01em",
                        }}
                      >
                        ※データ保護の観点から、削除操作と、クライアント情報の登録・更新は本エージェントでは実行できません。画面から直接お願いします。
                      </Typography>
                    </Box>
                  </Box>
                )}
                <AIChatInterface
                  open={isOpen}
                  onClose={() => {
                    // 閉じる時に BE 履歴を消す必要は無い (thread 単位で残しておく)。
                    // 縮小アニメーション後に unmount し、その後に状態をリセットする。
                    closeChat(() => {
                      dispatch(clearMessages());
                      setSelectedThreadId(undefined);
                      setIsExpanded(false);
                    });
                  }}
                  isExpanded={isExpanded}
                  primaryColor={formatTheme.colors.primary.main}
                  backgroundColor="#FFFFFF"
                  propertyId={propertyId}
                  onSendMessage={handleSendMessage}
                  onConfirmAction={handleConfirmAction}
                  onRejectAction={handleRejectAction}
                  onCancelSend={handleCancelSend}
                  onNewChat={handleNewChat}
                  messages={convertedMessages}
                  isSending={isSending}
                  hasMore={hasMore}
                  isLoadingMore={isLoadingMoreMessages}
                  onLoadMore={handleLoadMore}
                  isLoadingInitial={isLoading}
                  loadError={messagesError}
                  onRetry={() => {
                    if (selectedThreadId) {
                      dispatch(
                        getChatMessages({
                          thread_id: selectedThreadId,
                          limit: 20,
                        }),
                      );
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default AIChatButton;
